import { create } from 'zustand'
import type { ImportGroup, ImportedPlaceSummary } from '../api/bookmark'

/**
 * 첫 가입 온보딩: 지도 리스트 import → 가본 곳 체크 → 선택한 장소를 순서대로 랭킹.
 * 각 장소는 기존 StoreReview(카테고리 자동매핑 + 존 + A/B)를 redirect 체이닝으로 재사용한다.
 * (StoreReview/ReviewComplete는 수정하지 않음 — redirect 파라미터로 드라이버(/onboarding-rank)로 복귀)
 */
/**
 * 기록하러 떠나기 전 '가져오기' 화면이 들고 있던 것.
 *
 * 기록 줄에서 뒤로 나오면 이걸로 화면을 되살린다. 없으면 링크부터 다시
 * 넣어야 해서, 잘못 누른 대가가 너무 크다.
 */
export interface RankResumeState {
  /** 묶음을 고르던 중이었으면 그 묶음들, 체크리스트였으면 null */
  groups: ImportGroup[] | null
  title: string | null
  /** 묶음 종류와 공개 리스트 주인 — 되살릴 때 제목·안내 문구가 바뀌지 않게 */
  groupKind?: 'region' | 'list'
  ownerName?: string | null
  /** 묶음 하나를 들여다보던 중이었으면 그 묶음 id */
  openedGroupId: string | null
  /** 묶음 화면이 이름·주소를 찾아 쓰는 전체 장소 */
  pendingPlaces: ImportedPlaceSummary[] | null
  /** 체크리스트에 올라와 있던 장소 */
  places: ImportedPlaceSummary[]
  /** 체크돼 있던 장소 */
  selected: number[]
  /** 체크리스트에서 한 단계 뒤로 갈 때 돌아갈 묶음 화면 */
  lastGroups: {
    groups: ImportGroup[]
    title: string | null
    places: ImportedPlaceSummary[]
  } | null
}

interface OnboardingRankStore {
  /** 랭킹할 장소 큐 (가본 곳으로 체크된 place_id) */
  placeIds: number[]
  /** 다음에 랭킹할 큐 인덱스 */
  cursor: number
  /** 큐 진행 중 여부 (false면 드라이버는 import/체크 화면을 보여줌) */
  active: boolean
  /** 홈 로고로 진입했는지 — true면 완료/건너뛰기 후 연락처연동 대신 홈으로 */
  fromHome: boolean
  /** 기록을 떠나기 전 화면 상태. 뒤로 나왔을 때 되살린다. */
  resume: RankResumeState | null
  /** 지금까지 몇 곳을 기록했는지. 중간에 나왔을 때 알려 주려고 센다. */
  done: number
  start: (ids: number[], resume?: RankResumeState | null) => void
  setCursor: (n: number) => void
  setFromHome: (v: boolean) => void
  /** 한 곳을 기록하고 돌아왔을 때 */
  markDone: () => void
  /** 기록 줄만 끝내고 화면 상태는 남긴다 (뒤로 나왔을 때) */
  stop: () => void
  reset: () => void
}

export const useOnboardingRankStore = create<OnboardingRankStore>((set) => ({
  placeIds: [],
  cursor: 0,
  active: false,
  fromHome: false,
  resume: null,
  done: 0,
  start: (ids, resume = null) =>
    set({ placeIds: ids, cursor: 0, active: true, resume, done: 0 }),
  setCursor: (n) => set({ cursor: n }),
  setFromHome: (v) => set({ fromHome: v }),
  markDone: () => set((s) => ({ done: s.done + 1 })),
  // 줄만 끊는다. resume·done 은 남겨 둬야 화면을 되살리고 몇 곳인지 알려준다.
  stop: () => set({ placeIds: [], cursor: 0, active: false }),
  reset: () =>
    set({
      placeIds: [],
      cursor: 0,
      active: false,
      fromHome: false,
      resume: null,
      done: 0,
    }),
}))
